import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import { lightTheme, darkTheme } from '../theme/theme';
import type { Theme } from '@mui/material/styles';

const THEME_STORAGE_KEY = 'event-planner-theme-mode';

interface ThemeContextValue {
  isDarkMode: boolean;
  toggleTheme: () => void;
  theme: Theme;
}

const ThemeContext = createContext<ThemeContextValue>({
  isDarkMode: false,
  toggleTheme: () => {},
  theme: lightTheme,
});

export function useTheme() {
  return useContext(ThemeContext);
}

interface ThemeProviderProps {
  children: React.ReactNode;
}

/**
 * Admin theme context — provides dark mode toggle with localStorage persistence.
 *
 * Dark mode is not yet styled, so the system preference is ignored and
 * light mode is forced. When dark mode styling is implemented, restore
 * the useMediaQuery('(prefers-color-scheme: dark)') default and remove
 * the forced-light override below.
 *
 * This does NOT wrap MUI's ThemeProvider — that's done in main.tsx so the
 * MUI ThemeProvider can consume the theme from this context.
 */
export function AdminThemeProvider({ children }: ThemeProviderProps) {
  // Dark mode not yet styled — force light mode regardless of system preference.
  // TODO: restore system preference detection when dark mode is implemented:
  //   const systemPrefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored !== null) return stored === 'dark';
    return false;
  });

  const toggleTheme = useCallback(() => {
    setIsDarkMode((prev) => {
      const next = !prev;
      localStorage.setItem(THEME_STORAGE_KEY, next ? 'dark' : 'light');
      return next;
    });
  }, []);

  const theme = useMemo(() => (isDarkMode ? darkTheme : lightTheme), [isDarkMode]);

  const value = useMemo(
    () => ({ isDarkMode, toggleTheme, theme }),
    [isDarkMode, toggleTheme, theme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}
